import { ActivityIndicator, StyleSheet, View } from "react-native";
import CustomModal from "../../../common/general/CustomModal/CustomModal";
import CustomText from "../../../common/general/CustomText/CustomText";
import CustomSpacing from "../../../common/layout/CustomSpacing/CustomSpacing";
import FormLayout from "../common/FormLayout";

import type { GetAssessmentQuestionBankQuery } from "../../../../graphql/generated/graphql";

type AssessmentQuestionBank = NonNullable<
  NonNullable<GetAssessmentQuestionBankQuery["getAssessmentQuestionBank"]>["data"]
>;
export type QuestionBankAiTutor = AssessmentQuestionBank["aiTutors"][number];

export type AssessmentQuestionBankModalProps = {
  visible: boolean;
  onClose: () => void;
  isLoading: boolean;
  errorMessage?: string | null;
  aiTutors: QuestionBankAiTutor[];
};

const AssessmentQuestionBankModal = ({
  visible,
  onClose,
  isLoading,
  errorMessage,
  aiTutors,
}: AssessmentQuestionBankModalProps) => {
  const totalQuestions = aiTutors.reduce((sum, t) => sum + (t.questions?.filter(Boolean).length ?? 0), 0);
  const totalIles = aiTutors.length;
  const flattenedQuestions = aiTutors.flatMap((tutor) =>
    (tutor.questions ?? []).filter(Boolean).map((question, questionIndex) => ({ tutor, question, questionIndex }))
  );

  return (
    <CustomModal visible={visible} onRequestClose={onClose} ariaLabel="Assessment Question Bank">
      <FormLayout
        title="Question Bank"
        subtitle={`${totalIles} ILE${totalIles === 1 ? "" : "s"} • ${totalQuestions} question${totalQuestions === 1 ? "" : "s"}`}
        isSubmitting={false}
        onCancel={onClose}
        onSubmit={onClose}
        actionText="Back"
        hideCancelButton={true}
        buttonsContainerStyle={{ justifyContent: "flex-end" }}
        contentContainerStyle={{ paddingBottom: 0 }}>
        {isLoading ? (
          <View style={styles.loading}>
            <ActivityIndicator size="large" color="#0000ff" />
          </View>
        ) : (
          <>
            {!!errorMessage && (
              <>
                <CustomText text={errorMessage} useTranslationText={false} style={styles.errorText} />
                <CustomSpacing size="m" />
              </>
            )}

            <View style={styles.summaryPill}>
              <CustomText
                text={`${totalQuestions} question${totalQuestions === 1 ? "" : "s"} from ${totalIles} ILE${
                  totalIles === 1 ? "" : "s"
                }`}
                size="s"
                useTranslationText={false}
                style={styles.summaryText}
                weight="normal"
              />
            </View>
            <CustomSpacing size="m" />

            {aiTutors.length === 0 ? (
              <CustomText
                text="No tutors found for this assessment."
                useTranslationText={false}
                style={styles.emptyText}
              />
            ) : (
              flattenedQuestions.map(({ tutor, question }, idx) => {
                const rubrics = question.rubrics ?? [];
                const totalPoints =
                  typeof question.totalScore === "number"
                    ? question.totalScore
                    : rubrics.reduce((s, r) => s + (typeof r.points === "number" ? r.points : 0), 0);

                return (
                  <View key={question.id ?? `${tutor.id}-${idx}`} style={styles.questionCard}>
                    <View style={styles.questionCardHeader}>
                      <CustomText
                        text={`QUESTION ${idx + 1}`}
                        size="xs"
                        useTranslationText={false}
                        style={styles.questionIndex}
                        weight="normal"
                      />
                      <CustomText
                        text={question.question ?? ""}
                        size="m"
                        useTranslationText={false}
                        style={styles.questionTitle}
                        weight="normal"
                      />
                    </View>

                    <View style={styles.questionCardBody}>
                      <CustomText
                        text="RUBRIC CRITERIA"
                        size="xs"
                        useTranslationText={false}
                        style={styles.sectionLabel}
                        weight="normal"
                      />
                      <CustomSpacing size="s" />

                      {rubrics.length === 0 ? (
                        <CustomText
                          text="No rubrics available for this question."
                          useTranslationText={false}
                          style={styles.emptyText}
                        />
                      ) : (
                        <View style={styles.rubricsWrap}>
                          {rubrics.map((r) => (
                            <View key={r.id} style={styles.rubricRow}>
                              <CustomText
                                text={r.component ?? ""}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricText}
                                weight="normal"
                              />
                              <CustomText
                                text={`${r.points ?? 0} pts`}
                                size="s"
                                useTranslationText={false}
                                style={styles.rubricPoints}
                                weight="normal"
                              />
                            </View>
                          ))}
                        </View>
                      )}

                      <CustomSpacing size="m" />
                      <View style={styles.totalRow}>
                        <CustomText
                          text={`Total: ${totalPoints} pts`}
                          size="s"
                          useTranslationText={false}
                          style={styles.totalText}
                          weight="normal"
                        />
                      </View>
                    </View>

                    <View style={styles.questionCardFooter}>
                      <CustomText
                        text={`Source: ${tutor.title ?? "Interactive Learning Experience"}`}
                        size="s"
                        useTranslationText={false}
                        style={styles.footerText}
                        weight="normal"
                      />
                    </View>
                  </View>
                );
              })
            )}
          </>
        )}
      </FormLayout>
    </CustomModal>
  );
};

const styles = StyleSheet.create({
  errorText: {
    color: "#B42318",
  },
  emptyText: {
    color: "#667085",
  },
  loading: {
    paddingVertical: 32,
    alignItems: "center",
    justifyContent: "center",
  },
  summaryPill: {
    backgroundColor: "#F9FAFB",
    borderRadius: 12,
    paddingHorizontal: 14,
    paddingVertical: 12,
    borderWidth: 1,
    borderColor: "#F2F4F7",
  },
  summaryText: {
    color: "#344054",
  },
  questionCard: {
    borderWidth: 1,
    borderColor: "#EAECF0",
    borderRadius: 16,
    overflow: "hidden",
    marginBottom: 14,
    backgroundColor: "#FFFFFF",
  },
  questionCardHeader: {
    paddingHorizontal: 16,
    paddingVertical: 14,
    backgroundColor: "#F9FAFB",
    borderBottomWidth: 1,
    borderBottomColor: "#EAECF0",
  },
  questionIndex: {
    color: "#667085",
    letterSpacing: 0.6,
  },
  questionTitle: {
    color: "#101828",
    marginTop: 6,
    lineHeight: 22,
    fontSize: 16,
  },
  questionCardBody: {
    paddingHorizontal: 16,
    paddingVertical: 14,
  },
  rubricsWrap: {
    gap: 8,
  },
  sectionLabel: {
    color: "#667085",
    letterSpacing: 0.6,
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    backgroundColor: "#EEF4FF",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  rubricText: {
    color: "#101828",
    flex: 1,
  },
  rubricPoints: {
    color: "#3E68FE",
  },
  totalRow: {
    flexDirection: "row",
    justifyContent: "flex-end",
  },
  totalText: {
    color: "#344054",
  },
  questionCardFooter: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: "#F9FAFB",
    borderTopWidth: 1,
    borderTopColor: "#EAECF0",
  },
  footerText: {
    color: "#344054",
  },
});

export default AssessmentQuestionBankModal;
